import { useCallback, useEffect, useRef, useState } from 'react';
import { can } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

type Pt = { x: number; y: number };
interface LabelCamera {
  camera_id: string;
  name: string | null;
  city: string | null;
  version: number;
  status: string;
  assets: number;
  human_labels: number;
}
interface CameraView {
  cameraId: string;
  calibration: { version: number; status: string; line: { a: Pt; b: Pt; countedDirection: string }; roi: Pt[] };
  assets: { asset_id: string; window_start: string; duration_ms: number; labels: { labelId: string; source: string; labeler: string; crossings: number; at: string }[] | null }[];
  evaluations: { eval_id: string; calibration_version: number; label_source: string; summary: { windows: number; precision: number; recall: number; meanAbsCountError: number; windowsWithinOne: number }; result: string; reasons: string[]; created_at: string }[];
}
interface Mark {
  tMs: number;
  direction: 'A_TO_B' | 'B_TO_A';
}

/**
 * Unsaved marks are kept per video in this browser (a convenience only: nothing counts until it is saved
 * on the server). Storage may be unavailable (private mode); the page then works without drafts.
 */
const DRAFT = (assetId: string) => `urbanrats.labels.draft.v1.${assetId}`;
function loadDraft(assetId: string): Mark[] {
  try {
    const v = JSON.parse(localStorage.getItem(DRAFT(assetId)) ?? '[]') as Mark[];
    return Array.isArray(v) ? v.filter((m) => Number.isInteger(m.tMs) && (m.direction === 'A_TO_B' || m.direction === 'B_TO_A')) : [];
  } catch {
    return [];
  }
}
function saveDraft(assetId: string, marks: Mark[]): void {
  try {
    if (marks.length) localStorage.setItem(DRAFT(assetId), JSON.stringify(marks));
    else localStorage.removeItem(DRAFT(assetId));
  } catch {
    /* no storage: drafts are best effort */
  }
}

/**
 * Reference counting by a person (backend only). Our own locked video plays with the counting line drawn
 * over it; every crossing is marked with a key. Human labels are the only evidence that can make a line
 * VALIDATED; the evaluation (automatic count vs these labels) runs on the server.
 */
export function Labelling() {
  const { backend, user } = useCtx();
  const api = backend.server;
  const [cams, setCams] = useState<LabelCamera[] | null>(null);
  const [cam, setCam] = useState<string | null>(null);
  const [view, setView] = useState<CameraView | null>(null);
  const [asset, setAsset] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!api) return;
    void api.get<{ cameras: LabelCamera[] }>('/v1/admin/labels/cameras').then((r) => (r.ok ? setCams(r.value.cameras) : setErr(r.error)));
  }, [api, version]);
  useEffect(() => {
    if (!api || !cam) return;
    void api.get<CameraView>(`/v1/admin/labels/cameras/${encodeURIComponent(cam)}`).then((r) => (r.ok ? setView(r.value) : setErr(r.error)));
  }, [api, cam, version]);
  if (!api)
    return (
      <>
        <PageHead title="Count labelling">Reference counts are stored on the backend.</PageHead>
        <p className="empty" data-testid="labelling-demo-note">
          This page needs the UrbanRats backend. The demo has no locked live-camera videos to label.
        </p>
      </>
    );
  if (err) return <p role="alert" className="field-error">{err}</p>;
  if (!cams) return <p aria-busy="true">Loading…</p>;
  return (
    <>
      <PageHead title="Count labelling">
        Watch a locked video with the counting line and mark every vehicle that crosses it. Human labels are the only evidence that can make a line VALIDATED; the automatic count is compared with them on the server.
      </PageHead>
      <Section title="Calibrated cameras" testId="label-cameras">
        <DataTable<LabelCamera>
          rows={cams}
          rowKey={(c) => c.camera_id}
          empty="No calibrated camera."
          columns={[
            {
              key: 'c',
              header: 'Camera',
              cell: (c) => (
                <button type="button" className="linkish" onClick={() => (setCam(c.camera_id), setAsset(null))} data-testid={`label-cam-${c.camera_id}`}>
                  {c.name ?? c.camera_id}
                </button>
              ),
            },
            { key: 'v', header: 'Line', cell: (c) => <>v{c.version} <StateTag state={c.status} /></> },
            { key: 'a', header: 'Locked videos', numeric: true, cell: (c) => c.assets },
            { key: 'h', header: 'Human-labelled', numeric: true, cell: (c) => c.human_labels },
          ]}
        />
      </Section>
      {view && (
        <Section title={`Videos of ${view.cameraId}`} testId="label-assets">
          <DataTable
            rows={view.assets}
            rowKey={(x) => x.asset_id}
            empty="No locked videos yet (node apps/api/src/cli.ts calibration:sample <camera>)."
            columns={[
              {
                key: 'a',
                header: 'Window',
                cell: (x) => (
                  <button type="button" className="linkish" onClick={() => setAsset(x.asset_id)} data-testid={`label-asset-${x.asset_id}`}>
                    {fmtTime(x.window_start)} · {Math.round(x.duration_ms / 1000)} s
                  </button>
                ),
              },
              {
                key: 'l',
                header: 'Labels',
                cell: (x) => {
                  const draft = loadDraft(x.asset_id).length;
                  const saved = (x.labels ?? []).map((l) => `${l.source} ${l.labeler}: ${l.crossings}`).join(' · ');
                  return (
                    <>
                      {saved || '—'}
                      {draft > 0 && <span className="draft-tag"> · draft {draft} (not saved)</span>}
                    </>
                  );
                },
              },
            ]}
          />
          <p className="note">Evaluations (automatic count vs labels, run with node apps/api/src/cli.ts calibration:evaluate):</p>
          <DataTable
            rows={view.evaluations}
            rowKey={(e) => e.eval_id}
            empty="No evaluation yet."
            columns={[
              { key: 'r', header: 'Result', cell: (e) => <StateTag state={e.result === 'PASS' ? 'APPROVED' : 'FAILED'} /> },
              { key: 's', header: 'Labels', cell: (e) => e.label_source },
              { key: 'w', header: 'Windows', numeric: true, cell: (e) => e.summary.windows },
              { key: 'p', header: 'Precision · recall', numeric: true, cell: (e) => `${e.summary.precision.toFixed(3)} · ${e.summary.recall.toFixed(3)}` },
              { key: 'm', header: 'Mean |error| / window', numeric: true, cell: (e) => e.summary.meanAbsCountError.toFixed(2) },
              { key: 'x', header: 'Why', cell: (e) => e.reasons.join('; ') || '—' },
            ]}
          />
        </Section>
      )}
      {view && asset && <Labeller key={asset} assetId={asset} line={view.calibration.line} mayLabel={can(user, 'camera.manage')} onSaved={() => setVersion((n) => n + 1)} />}
    </>
  );
}

function Labeller({ assetId, line, mayLabel, onSaved }: { assetId: string; line: { a: Pt; b: Pt; countedDirection: string }; mayLabel: boolean; onSaved: () => void }) {
  const { backend } = useCtx();
  const api = backend.server!;
  const video = useRef<HTMLVideoElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [marks, setMarks] = useState<Mark[]>(() => loadDraft(assetId));
  const [restored] = useState(() => loadDraft(assetId).length);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<{ labelId: string; crossings: number; at: string } | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [keysLive, setKeysLive] = useState(() => document.hasFocus());
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0);
  const [flash, setFlash] = useState(false);
  const box = useRef<HTMLElement>(null);
  useEffect(() => {
    saveDraft(assetId, marks);
  }, [assetId, marks]);
  useEffect(() => {
    box.current?.scrollIntoView({ block: 'start' });
  }, []);
  useEffect(() => {
    const on = () => setKeysLive(document.hasFocus());
    window.addEventListener('focus', on);
    window.addEventListener('blur', on);
    const t = setInterval(on, 1000);
    return () => {
      window.removeEventListener('focus', on);
      window.removeEventListener('blur', on);
      clearInterval(t);
    };
  }, []);
  const [rate, setRate] = useState(0.5);
  const [complete, setComplete] = useState(false);
  const [noCrossings, setNoCrossings] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    void api.post<{ playlistUrl: string }>(`/v1/admin/round-media/${encodeURIComponent(assetId)}/preview`, {}).then((r) => (r.ok ? setUrl(`${api.apiBase}${r.value.playlistUrl}`) : setMsg(r.error)));
  }, [api, assetId]);
  useEffect(() => {
    const v = video.current;
    if (!v || !url) return;
    let h: import('hls.js').default | null = null;
    let dead = false;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        v.src = url;
        return;
      }
      h = new Hls();
      h.loadSource(url);
      h.attachMedia(v);
    });
    return () => {
      dead = true;
      h?.destroy();
    };
  }, [url]);
  useEffect(() => {
    if (video.current) video.current.playbackRate = rate;
  }, [rate, url]);
  // The line over the picture (object-fit: contain → letterbox offsets).
  const draw = useCallback(() => {
    const v = video.current;
    const c = canvas.current;
    if (!v || !c || !v.videoWidth) return;
    const r = v.getBoundingClientRect();
    c.width = r.width;
    c.height = r.height;
    const s = Math.min(r.width / v.videoWidth, r.height / v.videoHeight);
    const ox = (r.width - v.videoWidth * s) / 2;
    const oy = (r.height - v.videoHeight * s) / 2;
    const P = (p: Pt) => [ox + p.x * v.videoWidth * s, oy + p.y * v.videoHeight * s] as const;
    const g = c.getContext('2d')!;
    g.clearRect(0, 0, c.width, c.height);
    const [ax, ay] = P(line.a);
    const [bx, by] = P(line.b);
    g.strokeStyle = '#d7ff3f';
    g.lineWidth = 2;
    g.beginPath();
    g.moveTo(ax, ay);
    g.lineTo(bx, by);
    g.stroke();
    g.fillStyle = '#d7ff3f';
    g.font = 'bold 14px system-ui';
    g.fillText('A', ax + 6, ay);
    g.fillText('B', bx + 6, by);
    // Arrow: the counted direction crosses to the right-hand side of a→b.
    const mx = (ax + bx) / 2;
    const my = (ay + by) / 2;
    const len = Math.hypot(bx - ax, by - ay) || 1;
    const nx = -(by - ay) / len;
    const ny = (bx - ax) / len;
    const sgn = line.countedDirection === 'A_TO_B' ? 1 : -1;
    g.beginPath();
    g.moveTo(mx, my);
    g.lineTo(mx + nx * 40 * sgn, my + ny * 40 * sgn);
    g.stroke();
  }, [line]);
  useEffect(() => {
    const t = setInterval(draw, 500);
    return () => clearInterval(t);
  }, [draw]);
  const mark = useCallback((direction: Mark['direction']) => {
    const v = video.current;
    if (!v) return;
    setMarks((m) => [...m, { tMs: Math.round(v.currentTime * 1000), direction }].sort((x, y) => x.tMs - y.tMs));
    setSaved(null);
  }, []);
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement).tagName === 'INPUT') return;
      const v = video.current;
      if (!v) return;
      if (e.key === ' ') {
        e.preventDefault();
        if (v.paused) void v.play();
        else v.pause();
      } else if (e.key === 'a' || e.key === 'A') mark('A_TO_B');
      else if (e.key === 'b' || e.key === 'B') mark('B_TO_A');
      else if (e.key === 'ArrowLeft') v.currentTime = Math.max(0, v.currentTime - 2);
      else if (e.key === 'Backspace') {
        setMarks((m) => m.slice(0, -1));
        setSaved(null);
      }
    };
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [mark]);
  const save = async () => {
    setSaving(true);
    setSaveErr(null);
    const r = await api.post<{ labelId: string; crossings: number }>('/v1/admin/labels', { assetId, events: marks, complete, noCrossings: marks.length === 0 && noCrossings });
    setSaving(false);
    if (!r.ok) {
      setSaveErr(`Not saved: ${r.error}. Your marks are still here (draft kept in this browser).`);
      return;
    }
    // Only a server confirmation counts: the draft is cleared once the label id is back.
    setSaved({ ...r.value, at: new Date().toISOString() });
    saveDraft(assetId, []);
    onSaved();
  };
  return (
    <section className="section" data-testid="labeller" ref={box}>
      <h2>Label {assetId}</h2>
      {restored > 0 && !saved && (
        <p className="note" data-testid="draft-restored">
          Draft restored: {restored} unsaved mark(s) from earlier in this browser.
        </p>
      )}
      <p className="note">
        <strong>Click the picture</strong> at the moment a vehicle&apos;s bottom (where it touches the road) crosses the line: one click = one vehicle in the arrow&apos;s direction
        (Shift+click = the other way). A pickup with its trailer is one vehicle; two cars side by side are two. Keys also work when this page has the keyboard focus
        (<kbd>A</kbd>, <kbd>B</kbd>, <kbd>Space</kbd>, <kbd>←</kbd>, <kbd>Backspace</kbd>){keysLive ? '' : ' — right now it does not, so use clicks'}.
      </p>
      <div className="label-stage">
        <video
          ref={video}
          className="preview-video"
          muted
          playsInline
          onLoadedMetadata={(e) => ((e.currentTarget.playbackRate = rate), draw())}
          onPlay={(e) => ((e.currentTarget.playbackRate = rate), setPlaying(true))}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setPos(e.currentTarget.currentTime)}
          data-testid="label-video"
        />
        <canvas
          ref={canvas}
          className="label-overlay clickable"
          onClick={(e) => {
            mark(e.shiftKey ? 'B_TO_A' : 'A_TO_B');
            setFlash(true);
            setTimeout(() => setFlash(false), 350);
          }}
          data-testid="label-click"
          aria-label="Click to mark a crossing"
          role="button"
        />
        {flash && <div className="mark-flash" aria-hidden>+1</div>}
      </div>
      <div className="row-form form inline">
        <button type="button" className="btn" onClick={() => (video.current?.paused ? void video.current.play() : video.current?.pause())} data-testid="label-play">
          {playing ? 'Pause' : 'Play'}
        </button>
        <button type="button" className="btn" onClick={() => video.current && (video.current.currentTime = Math.max(0, video.current.currentTime - 2))}>
          −2 s
        </button>
        <button type="button" className="btn" onClick={() => video.current && (video.current.currentTime = 0)}>
          Restart
        </button>
        <span className="muted">{pos.toFixed(1)} s</span>
        {[0.25, 0.5, 1].map((r) => (
          <button key={r} type="button" className={`chip ${rate === r ? 'on' : ''}`} onClick={() => setRate(r)}>
            ×{r}
          </button>
        ))}
        <button type="button" className="btn" onClick={() => mark('A_TO_B')} data-testid="mark-a">
          Mark A→B
        </button>
        <button type="button" className="btn" onClick={() => mark('B_TO_A')}>
          Mark B→A
        </button>
        <button type="button" className="btn" onClick={() => (setMarks((m) => m.slice(0, -1)), setSaved(null))} disabled={!marks.length} data-testid="label-undo">
          Undo last
        </button>
      </div>
      <p data-testid="label-count">
        {marks.length} marked: {marks.map((m) => `${(m.tMs / 1000).toFixed(1)}s${m.direction === 'B_TO_A' ? '←' : ''}`).join(' · ') || '—'}
      </p>
      {mayLabel ? (
        <div className="form inline">
          <label>
            <input type="checkbox" checked={complete} onChange={(e) => setComplete(e.target.checked)} data-testid="label-complete" /> I watched the whole video and marked every crossing
          </label>
          {marks.length === 0 && pos > 5 && (
            <p className="warn-box" data-testid="label-watched-empty">
              You watched {pos.toFixed(0)} s but nothing is marked. If vehicles passed, the marks did not register: click the picture at each crossing.
            </p>
          )}
          {marks.length === 0 && (
            <label className="warn-box" data-testid="label-zero">
              <input type="checkbox" checked={noCrossings} onChange={(e) => setNoCrossings(e.target.checked)} data-testid="label-no-crossings" /> Nothing is marked. Tick only if NO vehicle crossed the line in
              this whole video (otherwise the marks did not reach the page: click the video and use the keys).
            </label>
          )}
          <button type="button" className="btn" disabled={!complete || saving || !!saved || (marks.length === 0 && !noCrossings)} onClick={() => void save()} data-testid="label-save">
            {saving ? 'Saving…' : saved ? 'Saved' : `Save ${marks.length} reference label(s)`}
          </button>
        </div>
      ) : (
        <p className="note">Your role can view but not save labels.</p>
      )}
      {msg && <p role="alert" className="field-error">{msg}</p>}
      {saveErr && (
        <p role="alert" className="field-error" data-testid="label-error">
          {saveErr}
        </p>
      )}
      {saved && (
        <div className="ok-box" role="status" data-testid="label-saved">
          <strong>Saved on the server.</strong> {saved.crossings} crossing(s) for this video as HUMAN reference labels ({saved.labelId}), {fmtTime(saved.at)}. The draft was cleared. To correct it, mark again and save: the newest human label counts.
        </div>
      )}
    </section>
  );
}
